/**
 * A custom element that progressively reveals feed items.
 *
 * Every direct child except the load-more control is treated as one item.
 *
 * @extends {HTMLElement}
 */
class EditorialFeed extends HTMLElement {
  /** @type {number | undefined} */
  initializationFrame;

  /** @type {HTMLElement[]} */
  feedItems = [];

  /** @type {HTMLElement | null} */
  loadMoreControl = null;

  /** @type {HTMLButtonElement | null} */
  loadMoreButton = null;

  connectedCallback() {
    if (this.initializationFrame) return;

    // Waiting one frame also covers instances added dynamically in the theme editor.
    this.initializationFrame = requestAnimationFrame(() => {
      this.initializationFrame = undefined;
      if (this.isConnected) this.#initialize();
    });
  }

  disconnectedCallback() {
    if (this.initializationFrame) cancelAnimationFrame(this.initializationFrame);
    this.initializationFrame = undefined;
  }

  #initialize() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.feedItems = Array.from(this.children).filter(
      (child) => child instanceof HTMLElement && !child.hasAttribute('data-editorial-load-more')
    );
    this.loadMoreControl = this.querySelector(':scope > [data-editorial-load-more]');
    this.loadMoreButton = this.loadMoreControl?.querySelector('[data-editorial-load-more-button]') ?? null;

    const loadMoreEnabled = this.dataset.loadMoreEnabled !== 'false';
    if (!loadMoreEnabled) {
      this.feedItems.forEach((item) => {
        item.hidden = false;
      });

      if (this.loadMoreControl) this.loadMoreControl.hidden = true;
      return;
    }

    const configuredCount = Number.parseInt(this.dataset.initialCount ?? '', 10);
    const initialCount = Number.isFinite(configuredCount) ? Math.max(configuredCount, 1) : 1;

    this.#applyCollapsedState(initialCount);

    this.loadMoreButton?.addEventListener('click', this.#revealAll);

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.#handleBlockSelect);
      this.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);
    }
  }

  /** @param {number} initialCount */
  #applyCollapsedState(initialCount) {
    this.feedItems.forEach((item, index) => {
      item.hidden = index >= initialCount;
    });

    this.#updateLoadMoreVisibility();
  }

  #updateLoadMoreVisibility() {
    const hasHiddenItems = this.feedItems.some((item) => item.hidden);
    if (this.loadMoreControl) this.loadMoreControl.hidden = !hasHiddenItems;
  }

  #revealAll = () => {
    this.feedItems.forEach((item) => {
      item.hidden = false;
    });

    this.#updateLoadMoreVisibility();
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const target = event.target;
    const selectedItem = this.feedItems.find(
      (item) => target instanceof Node && (item === target || item.contains(target))
    );

    if (!selectedItem?.hidden) return;

    selectedItem.hidden = false;
    this.#updateLoadMoreVisibility();
  };

  #handleBlockDeselect = () => {
    const configuredCount = Number.parseInt(this.dataset.initialCount ?? '', 10);
    const initialCount = Number.isFinite(configuredCount) ? Math.max(configuredCount, 1) : 1;

    this.#applyCollapsedState(initialCount);
  };
}

if (!customElements.get('editorial-feed')) {
  customElements.define('editorial-feed', EditorialFeed);
}
